import { formatInTimeZone } from 'date-fns-tz'

// `open`/`close` son opcionales a propósito: el editor puede persistir un día
// como `{ closed: false }` sin horas (toggle "Abierto" sin completar campos). El
// render público debe tolerar ese estado parcial — un horario a medias NO debe
// tumbar el microsite. Usa `dayHasHours` antes de leer open/close.
export type DayHours = { open?: string; close?: string; closed?: boolean }
export type OperatingHours = Record<string, DayHours>

/** Un día cuenta como "abierto" solo si no está marcado cerrado y tiene ambas
 *  horas presentes. Filtra datos parciales (`{closed:false}` sin horas) que de
 *  otro modo reventarían parseHM/formatTime12h con "undefined.split". */
export function dayHasHours(
  day: DayHours | undefined | null,
): day is { open: string; close: string; closed?: boolean } {
  return !!day && !day.closed && !!day.open && !!day.close
}

export type OpenStatus = {
  /** El local está físicamente abierto en este momento. */
  isOpen: boolean
  /** El local está abierto Y todavía dentro de la ventana para tomar pedidos (antes del corte por buffer). */
  isAcceptingOrders: boolean
  /** Hora de cierre ("HH:mm") del intervalo activo, o null si está cerrado. */
  closesAt: string | null
  /** Próxima apertura cuando está cerrado: { day: 'monday'..'sunday', time: 'HH:mm' }. */
  nextOpen: { day: string; time: string } | null
  /** Minutos hasta el cierre cuando está abierto; null si está cerrado. Para el conteo regresivo. */
  minutesUntilClose: number | null
  /** Minutos hasta la próxima apertura cuando está cerrado; null si está abierto. Para el conteo regresivo. */
  minutesUntilOpen: number | null
  /** La próxima apertura es hoy mismo (cambia el fraseo: "Abre hoy" vs "Abre el {día}"). */
  opensToday: boolean
}

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const ALWAYS_OPEN: OpenStatus = {
  isOpen: true,
  isAcceptingOrders: true,
  closesAt: null,
  nextOpen: null,
  minutesUntilClose: null,
  minutesUntilOpen: null,
  opensToday: false,
}

/** "HH:mm" -> minutos desde medianoche. Tolera valores ausentes/inválidos
 *  (devuelve 0) como defensa extra; los callers ya filtran con `dayHasHours`. */
function parseHM(value: string | undefined | null): number {
  if (!value) return 0
  const [h, m] = value.split(':').map((n) => parseInt(n, 10))
  return (h || 0) * 60 + (m || 0)
}

function prevDay(day: string): string {
  const idx = DAY_ORDER.indexOf(day)
  return DAY_ORDER[(idx - 1 + 7) % 7]
}

type Active = { closeHM: string; minutesUntilClose: number }

/** Intervalo abierto activo (si existe) para el momento dado, contemplando cruce de medianoche. */
function activeInterval(
  oh: OperatingHours,
  dayName: string,
  nowMin: number,
): Active | null {
  const today = oh[dayName]
  if (dayHasHours(today)) {
    const o = parseHM(today.open)
    const c = parseHM(today.close)
    if (c > o && nowMin >= o && nowMin < c) {
      return { closeHM: today.close, minutesUntilClose: c - nowMin }
    }
    // Cruza medianoche (cierra al día siguiente) y aún estamos antes de medianoche.
    if (c < o && nowMin >= o) {
      return { closeHM: today.close, minutesUntilClose: 1440 - nowMin + c }
    }
  }

  // Madrugada que pertenece al día anterior (ayer cruzaba medianoche).
  const prev = oh[prevDay(dayName)]
  if (dayHasHours(prev)) {
    const o = parseHM(prev.open)
    const c = parseHM(prev.close)
    if (c < o && nowMin < c) {
      return { closeHM: prev.close, minutesUntilClose: c - nowMin }
    }
  }

  return null
}

function findNextOpen(
  oh: OperatingHours,
  dayName: string,
  nowMin: number,
): { day: string; time: string } | null {
  // ¿Abre hoy más tarde?
  const today = oh[dayName]
  if (dayHasHours(today) && nowMin < parseHM(today.open)) {
    return { day: dayName, time: today.open }
  }
  // Siguiente día con apertura.
  const idx = DAY_ORDER.indexOf(dayName)
  for (let i = 1; i <= 7; i++) {
    const d = DAY_ORDER[(idx + i) % 7]
    const h = oh[d]
    if (dayHasHours(h)) return { day: d, time: h.open }
  }
  return null
}

/**
 * Calcula el estado de apertura de un local desde su `operating_hours` y zona horaria.
 *
 * Fail-open: si no hay horario configurado (null o vacío) el local se considera
 * siempre abierto para no bloquear pedidos de merchants que no lo configuraron.
 *
 * @param cutoffMinutes  Minutos antes del cierre en que se deja de aceptar pedidos (last call).
 * @param now            Instante a evaluar (inyectable para testabilidad).
 */
export function getOpenStatus(
  operatingHours: OperatingHours | null,
  timezone: string,
  cutoffMinutes: number,
  now: Date,
): OpenStatus {
  if (!operatingHours || Object.keys(operatingHours).length === 0) {
    return ALWAYS_OPEN
  }

  const tz = timezone || 'America/Chicago'
  const dayName = formatInTimeZone(now, tz, 'EEEE').toLowerCase()
  const nowMin = parseHM(formatInTimeZone(now, tz, 'HH:mm'))

  const active = activeInterval(operatingHours, dayName, nowMin)

  if (!active) {
    const nextOpen = findNextOpen(operatingHours, dayName, nowMin)
    let minutesUntilOpen: number | null = null
    let opensToday = false
    if (nextOpen) {
      const openMin = parseHM(nextOpen.time)
      if (nextOpen.day === dayName && openMin > nowMin) {
        // Abre más tarde hoy.
        minutesUntilOpen = openMin - nowMin
        opensToday = true
      } else {
        // Día futuro (findNextOpen ya descartó "hoy más tarde"; un día igual aquí
        // significa la próxima semana).
        const delta = (DAY_ORDER.indexOf(nextOpen.day) - DAY_ORDER.indexOf(dayName) + 7) % 7
        const daysAhead = delta === 0 ? 7 : delta
        minutesUntilOpen = daysAhead * 1440 - nowMin + openMin
      }
    }
    return {
      isOpen: false,
      isAcceptingOrders: false,
      closesAt: null,
      nextOpen,
      minutesUntilClose: null,
      minutesUntilOpen,
      opensToday,
    }
  }

  return {
    isOpen: true,
    isAcceptingOrders: active.minutesUntilClose > cutoffMinutes,
    closesAt: active.closeHM,
    nextOpen: null,
    minutesUntilClose: active.minutesUntilClose,
    minutesUntilOpen: null,
    opensToday: false,
  }
}
